import { useEffect, useMemo, useRef } from "react";
import { useUI } from "@context/ui-managed-context";
import { useAppContext } from "@context/app";
import { createHtmlPortalNode } from "react-reverse-portal";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import {
  LessonStep,
  OriginalLesson,
  SelectedStep,
  WatchTimeData,
  LESSON_TYPE,
  ICourse,
  StepInfo,
  LESSON_STEP_TYPE,
} from "@types";
import homeApi from "@features/home/helpers/homeApi";
import courseApi from "@api/course";
import { useAccessContext } from "../context";
import { useRouter } from "next/router";
import { modalActions } from "@context/modals";
import { MODAL_TYPES } from "types/modals";
import { BANNER_HEIGHT, BANNER_OFFSET_HIDE } from "constants/layout-consts";
import { RateModalStep } from "@modules/layout/modal/star-rate";
import { VISITING_STATUS_LOCAL_STORAGE_KEY } from "@utils/constants";
import { useLayoutContext } from "@context/layout";

export const useAccess = () => {
  const isMobile = useIsMobile(1024);
  const router = useRouter();
  const { registered: userRegistered } = router.query;
  const {
    showFestivalBanner,
    user: { isLoggedIn },
  } = useAppContext();
  const { openAlert, setAlertView, alertView } = useUI();
  const { subscriptionData } = useLayoutContext();

  const portalNode = useMemo(() => {
    return typeof window !== "undefined" ? createHtmlPortalNode() : null;
  }, []);

  const {
    mutate,
    courseData,
    loaded,
    setLoaded,
    stopAutoPlayLast,
    setStopAutoPlayLast,
    scrolled,
    setScrolled,
    theaterMode,
    isExamResult,
    setIsResult,
    setFavCourseStatus,
    requestNext,
    setRequestNext,
    selectedLesson,
    setSelectedLesson,
    favoriteLoading,
    setFavoriteLoading,
    recentProduct,
    recommendedProduct,
    relatedProduct,
    instructorProduct,
    comments,
    setComments,
    prevLesson,
    setPrevLesson,
    videoTime,
    setVideoTime,
    setCourseData,
    instructorPackage,
    relatedPackage,
    isSidebarOpen,
    setIsSidebarOpen,
    watchLessonCounter,
    setWatchLessonCounter,
    isBottomExamVisible,
    setisBottomExamVisible,
    jumpVideoTime,
    setJumpVideoTime,
    setShowNoteTab,
    showNoteTab,
    playerRef,
  } = useAccessContext();

  const {
    product: { id },
    original_lessons,
    user: { is_favorite },
  } = courseData;

  const watchedData = useRef<WatchTimeData>({
    lastWatchedTime: 0,
    watchedDuration: 0,
    lastWatchedPercent: 0,
  });
  const layoutData = useLayoutContext();

  ///set next lesson in next lesson or next lesson in next chapter
  const setStepNextLesson = (currentStep: number) =>
    lesson
      ? currentStep < lesson.steps.length - 1
        ? { lessonIdx: lessonIdx, stepIdx: currentStep + 1 }
        : lessonIdx < original_lessons?.length - 1
          ? original_lessons[lessonIdx + 1].steps.length
            ? { lessonIdx: lessonIdx + 1, stepIdx: 0 }
            : undefined
          : undefined
      : undefined;

  const hasLongHeader = showFestivalBanner && !scrolled;
  const { isAdmin, guestSessionId } = layoutData;
  const { lessonIdx, stepIdx } = selectedLesson;

  const lesson =
    lessonIdx < original_lessons?.length
      ? original_lessons[lessonIdx]
      : original_lessons[0] || undefined;
  const step =
    lesson && stepIdx < lesson.steps.length
      ? lesson.steps[stepIdx]
      : lesson?.steps?.[0] || undefined;
  const isExam = step?.type === LESSON_TYPE.EXAM;

  //exam just for admin user

  const currentLesson = original_lessons?.[lessonIdx] || original_lessons[0];
  const nextLesson: SelectedStep | undefined = currentLesson
    ? currentLesson.steps[stepIdx + 1]?.type === LESSON_TYPE.EXAM && !isAdmin
      ? setStepNextLesson(stepIdx + 1)
      : setStepNextLesson(stepIdx)
    : undefined;

  const examLesson = useMemo(() => {
    if (!original_lessons) return null;

    for (let i = 0; i < original_lessons.length; i++) {
      const lesson = original_lessons[i];

      const stepIndex = lesson.steps.findIndex(
        (stepItem) =>
          stepItem.type === LESSON_TYPE.EXAM &&
          (stepItem.info as StepInfo).type === LESSON_STEP_TYPE.GENERAL
      );

      if (stepIndex >= 0) {
        return { step: stepIndex, lessonIdx: i };
      }
    }

    return null;
  }, [original_lessons]);

  const nextIsLast =
    nextLesson &&
    nextLesson.lessonIdx === original_lessons.length - 1 &&
    nextLesson.stepIdx ===
      original_lessons[nextLesson.lessonIdx].steps.length - 1;
  const isFirstLesson = nextLesson?.lessonIdx === 0 && nextLesson.stepIdx == 1;

  const setFavoriteAction = async () => {
    if (!isLoggedIn) {
      modalActions.addModal(MODAL_TYPES.NEW_LOGIN_ALERT, {
        config: { hideCloseIcon: true },
      });
      return;
    }

    setFavoriteLoading(true);
    return homeApi[
      is_favorite ? "removeFavoriteProduct" : "addFavoriteProduct"
    ](id)
      .fetch()
      .then((data) => {
        alertView.alertContent.push({
          mode: data.status ? (is_favorite ? "warning" : "success") : "danger",
          textBody: data.message,
        });
        setAlertView("SNACKBAR", alertView.alertContent);
        openAlert();
        const result = is_favorite ? false : true;
        setCourseData({
          ...courseData,
          user: { ...courseData.user, is_favorite: result },
        });
      })
      .catch((error) => {
        alertView.alertContent.push({
          mode: "danger",
          textBody:
            error?.message || error?.error?.message || "دوباره تلاش کنید",
        });
        setAlertView("SNACKBAR", alertView.alertContent);
        openAlert();
      })
      .finally(() => {
        setFavoriteLoading(false);
      });
  };

  const copyCourseLink = () => {
    alertView.alertContent.push({
      mode: "success",
      textBody: "لینک آموزش با موفقیت کپی شد.",
    });
    setAlertView("SNACKBAR", alertView.alertContent);
    openAlert();
  };

  const submitRate = async (rate: 1 | 2 | 3 | 4 | 5 | null) => {
    return await courseApi
      .productRate(id, { rate })
      .fetch()
      .then((data) => {
        setCourseData({
          ...courseData,
          user: {
            ...courseData.user,
            rating: {
              ...courseData.user.rating,
              rate,
            },
          },
          product: {
            ...courseData.product,
            rating: {
              ...courseData.product.rating,
              rate: data.product_rating,
            },
          },
        });

        return data;
      })
      .then((response) => {
        alertView.alertContent = [
          {
            mode: "success",
            textBody: response.message || "نظر شما با موفقیت ثبت شد.",
          },
        ];
        setAlertView("SNACKBAR", alertView.alertContent);
        openAlert();
        return true;
      })
      .catch((error) => {
        setAlertView("ALERT", [{ mode: "danger", textBody: error.message }]);
        openAlert();
        return false;
      });
  };

  const submitComment = async (comment: string) => {
    return await courseApi
      .postComment({
        comment_post_ID: String(id),
        comment,
      })
      .fetch()
      .then((response) => {
        alertView.alertContent = [
          {
            mode: response.status ? "success" : "danger",
            textBody: response.message || "نظر شما با موفقیت ثبت شد.",
          },
        ];
        setAlertView("SNACKBAR", alertView.alertContent);
        openAlert();
        if (comments?.comments.length)
          setComments({
            ...comments,
            comments: [
              {
                children: [],
                created_at: new Date().toISOString(),
                id: response.data.comment.id,
                message: comment,
                name:
                  (layoutData?.userData?.user?.first_name ?? "") +
                  (layoutData?.userData?.user?.last_name ?? ""),
                role: "user",
              },
              ...(comments?.comments || []),
            ],
          });
        else {
          setComments({
            comments: [
              {
                children: [],
                created_at: new Date().toISOString(),
                id: response.data.comment.id,
                message: comment,
                name:
                  (layoutData?.userData?.user?.first_name ?? "") +
                  (layoutData?.userData?.user?.last_name ?? ""),
                role: "user",
              },
            ],
            total: 1,
            loadMore: false,
          });
        }
        return true;
      })
      .catch((error) => {
        setAlertView("ALERT", [{ mode: "danger", textBody: error.message }]);
        openAlert();
        return false;
      });
  };

  const onSetSubmitRate = (withComment = false) => {
    if (playerRef.current?.pausePlayer) playerRef.current.pausePlayer();
    modalActions.addModal(MODAL_TYPES.STAR_RATE_COURSE, {
      config: {
        hideCloseIcon: true,
      },
      data: {
        submit: async ({
          mode,
          data,
        }: {
          mode: "comment" | "rate";
          data: number | string | null;
        }) => {
          if (mode === "comment") return submitComment(data as string);
          else return submitRate(data as 1 | 2 | 3 | 4 | 5 | null);
        },
        initialRateValue: courseData.user.rating?.rate || null,
        initialStep: courseData.user.rating?.rate
          ? RateModalStep.SUBMIT_COMMENT
          : RateModalStep.SUBMIT_RATE,
        withComment: withComment,
      },
    });
  };

  const onUpdateSubmitRate = () => {
    modalActions.addModal(MODAL_TYPES.STAR_RATE_COURSE, {
      config: {
        hideCloseIcon: true,
      },
      data: {
        submit: async ({
          mode,
          data,
        }: {
          mode: "comment" | "rate";
          data: number | string | null;
        }) => {
          return (await (mode === "comment"))
            ? submitComment(data as string)
            : submitRate(data as 1 | 2 | 3 | 4 | 5 | null);
        },
        initialRateValue: courseData.user.rating?.rate || null,
        initialStep: RateModalStep.SUBMIT_RATE,
        withComment: false,
      },
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < BANNER_OFFSET_HIDE + BANNER_HEIGHT)
        setScrolled((prev) => (!prev ? true : prev));
      else setScrolled((prev) => (prev ? false : prev));
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    watchedData.current.lastWatchedTime = 0;
    watchedData.current.watchedDuration = 0;
    watchedData.current.lastWatchedPercent = step ? step.watch_percent : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonIdx, stepIdx]);

  const updateWatchTime = async (time: number, duration: number) => {
    if (!step) return;
    if (step.is_constant) return;
    try {
      if (watchedData.current.req) watchedData.current.req.cancel();

      const payload = {
        duration: Math.round(duration),
        time: Math.round(time),
        lesson_id: String(step.id),
        product_id: String(id),
      };

      const isGuest = !isLoggedIn;

      if (isGuest) {
        if (!guestSessionId) return;
        watchedData.current.req = courseApi.updateGuestWatchTime({
          ...payload,
          session_id: guestSessionId,
        });
      } else watchedData.current.req = courseApi.updateWatchTime(payload);

      await watchedData.current.req.fetch();
      const p = Math.max(Math.min(100, Math.round((time * 100) / duration)), 0);
      watchedData.current.lastWatchedPercent = Math.max(
        watchedData.current.lastWatchedPercent,
        p
      );
      onMutateWatchPercent(
        selectedLesson.lessonIdx,
        selectedLesson.stepIdx,
        watchedData.current.lastWatchedPercent
      );
    } catch (error) {
    } finally {
      watchedData.current.req = undefined;
    }
  };

  const timeUpdateHandler = async (time: number, duration: number) => {
    const diff = time - watchedData.current.lastWatchedTime;
    if (diff <= 1 && diff > 0)
      watchedData.current.watchedDuration +=
        time - watchedData.current.lastWatchedTime;
    if (duration > 0 && watchedData.current.watchedDuration >= 30) {
      watchedData.current.watchedDuration = 0;
      await updateWatchTime(time, duration);
    }
    watchedData.current.lastWatchedTime = time;
    setVideoTime(time);
    if (!loaded && stopAutoPlayLast) {
      playerRef.current?.togglePlayHandler();
      setStopAutoPlayLast(false);
    }
  };

  const lessonChangeHandler = async (
    lesson: SelectedStep,
    config: { withConfirm: boolean; timeVideo: number; scroll: boolean }
  ) => {
    const { withConfirm, timeVideo, scroll } = config;
    if (
      step?.type === LESSON_TYPE.EXAM &&
      !isMobile &&
      !isExamResult &&
      withConfirm
    ) {
      modalActions.addModal(MODAL_TYPES.EXIT_EXAM, {
        config: {
          hideCloseIcon: true,
        },
        data: {
          onSubmit: () => {
            modalActions.clearModals();
            setPrevLesson(selectedLesson);
            setSelectedLesson(lesson);
            setVideoTime(0);

            if (!loaded) setLoaded(true);
            setTimeout(() => {
              window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            }, 100);
          },
        },
      });

      return;
    }

    setPrevLesson(selectedLesson);
    setSelectedLesson(lesson);
    setVideoTime(0);

    if (
      typeof lesson.lessonIdx === "number" &&
      typeof lesson.stepIdx === "number"
    ) {
      const collectionNewVal = lesson.lessonIdx + 1;
      const lessonNewVal = lesson.stepIdx + 1;
      router.query.collection = String(collectionNewVal);
      router.query.lesson = String(lessonNewVal);

      const currentRecentVisits = localStorage.getItem(
        VISITING_STATUS_LOCAL_STORAGE_KEY
      );
      const newDesiredVal = {
        id,
        collection: collectionNewVal,
        lesson: lessonNewVal,
      };
      if (currentRecentVisits) {
        const currentVal: {
          id: number;
          collection: number;
          lesson: number;
        }[] = JSON.parse(currentRecentVisits);

        if (Array.isArray(currentVal)) {
          const existingIndex = currentVal.findIndex((cv) => cv.id === id);
          if (existingIndex !== -1) {
            const copy = [...currentVal];
            copy[existingIndex] = newDesiredVal;
            localStorage.setItem(
              VISITING_STATUS_LOCAL_STORAGE_KEY,
              JSON.stringify(copy)
            );
          } else {
            const copy = [...currentVal, newDesiredVal];
            if (copy.length > 8) copy.pop();
            localStorage.setItem(
              VISITING_STATUS_LOCAL_STORAGE_KEY,
              JSON.stringify(copy)
            );
          }
        } else
          localStorage.setItem(
            VISITING_STATUS_LOCAL_STORAGE_KEY,
            JSON.stringify([newDesiredVal])
          );
      } else
        localStorage.setItem(
          VISITING_STATUS_LOCAL_STORAGE_KEY,
          JSON.stringify([newDesiredVal])
        );

      router.push(
        { pathname: router.pathname, query: router.query },
        undefined,
        {
          shallow: true,
        }
      );
    }

    setStopAutoPlayLast(true);
    setTimeout(() => {
      if (scroll) window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      if (videoTime) {
        setJumpVideoTime(timeVideo);
        setLoaded(true);
      }
    }, 100);
  };

  const ScrollTop = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }, 100);
  };

  const goNext = () => {
    if (nextLesson) {
      if (nextIsLast) {
        setLoaded(false);
        setStopAutoPlayLast(false);
      } else setLoaded(true);
      setRequestNext(true);
      setPrevLesson(selectedLesson);
      setSelectedLesson({
        lessonIdx: nextLesson.lessonIdx,
        stepIdx: nextLesson.stepIdx,
      });

      setWatchLessonCounter((prev) => prev + 1);
      const stored = Number(localStorage.getItem("watchShowModalCounter"));
      if (
        watchLessonCounter > 2 &&
        isMobile &&
        courseData.user.rating?.rate === null &&
        stored < 2
      ) {
        onSetSubmitRate(true);
        setWatchLessonCounter(0);
        localStorage.setItem("watchShowModalCounter", String(stored + 1));
      }
    }

    ScrollTop();
  };

  const goPrevLesson = () => {
    if (selectedLesson.lessonIdx === 0 && selectedLesson.stepIdx === 0) return;
    const { lessonIdx, stepIdx } = selectedLesson;
    setPrevLesson(selectedLesson);
    const currentLessonIdx = stepIdx ? lessonIdx : lessonIdx - 1;
    const currentStepIdx = stepIdx
      ? stepIdx - 1
      : original_lessons[lessonIdx - 1].steps.length - 1;
    setSelectedLesson({
      lessonIdx: currentLessonIdx,
      stepIdx: currentStepIdx,
    });
    ScrollTop();
  };

  const onMutateWatchPercent = (
    lessonIdx: number,
    stepIdx: number,
    percent: number
  ) => {
    const newCourseData: ICourse = {
      ...courseData,
      original_lessons: [...original_lessons],
    };
    const lesson: OriginalLesson = {
      ...original_lessons[lessonIdx],
      steps: [...original_lessons[lessonIdx].steps],
    };
    const step: LessonStep = {
      ...lesson.steps[stepIdx],
      watch_percent: percent,
    };

    lesson.steps[stepIdx] = step;
    newCourseData.original_lessons[lessonIdx] = lesson;

    setCourseData(newCourseData);
  };
  ///cal in exam lesson
  const onPrevLesson = (withConfirm: boolean = true) => {
    if (prevLesson)
      lessonChangeHandler(prevLesson, {
        withConfirm: withConfirm,
        timeVideo: 0,
        scroll: true,
      });
    else {
      goPrevLesson();
    }
  };
  const onEnd = async (time: number, duration: number) => {
    watchedData.current.watchedDuration = 0;
    await updateWatchTime(time, duration);
    watchedData.current.lastWatchedTime = time;
  };
  const clickNoteCourse = (time: number, noteLessonId: number) => {
    if (step?.id === noteLessonId) {
      setJumpVideoTime(time);
      setTimeout(() => {
        ScrollTop();
      }, 100);
    } else {
      const _lessonIdx = original_lessons.findIndex((lesson) =>
        lesson.steps.some((step) => step.id === noteLessonId)
      );

      if (_lessonIdx !== -1) {
        const _stepIdx = original_lessons[_lessonIdx].steps.findIndex(
          (step) => Number(step.id) === Number(noteLessonId)
        );

        lessonChangeHandler(
          {
            lessonIdx: _lessonIdx,
            stepIdx: _stepIdx,
          },
          { withConfirm: false, scroll: true, timeVideo: time }
        );
      }
    }
  };

  const findPreviousLessonStep = (
    lessonIdx: number,
    stepIdx: number
  ): { lessonIdx: number; stepIdx: number } => {
    if (stepIdx > 0) {
      stepIdx -= 1;
    } else {
      lessonIdx -= 1;
      if (lessonIdx < 0) return { lessonIdx: 0, stepIdx: 0 };
      stepIdx = original_lessons[lessonIdx]?.steps?.length - 1;
    }
    const step = original_lessons[lessonIdx]?.steps[stepIdx];

    if (step.type === LESSON_TYPE.LESSON) {
      return { lessonIdx, stepIdx };
    }

    return findPreviousLessonStep(lessonIdx, stepIdx);
  };
  const onPrevLessonHeadline = () => {
    const { lessonIdx, stepIdx } = selectedLesson;
    if (lessonIdx === 0 && stepIdx === 0) {
      router.back();
      return;
    }
    const result = findPreviousLessonStep(lessonIdx, stepIdx);
    setSelectedLesson(result);
  };

  const handleCertificatePreviewModal = () => {
    if (playerRef.current) {
      playerRef.current.pausePlayer();
    }
    modalActions.addModal(MODAL_TYPES.CERTIFICATE_PREVIEW, {
      config: {
        modalClassName: "overflow-y-auto",
        hideCloseIcon: true,
      },
      data: {
        watch_times: courseData.user.watch_times?.total || 0,
        closeCertificateModal: () => {
          if (
            courseData.user.watch_times?.total === 100 &&
            examLesson?.lessonIdx
          ) {
            setSelectedLesson({
              lessonIdx: examLesson.lessonIdx,
              stepIdx: examLesson.step,
            });
          }
        },
      },
    });
  };

  const shouldRenderSubscriptionTopBanner = !subscriptionData?.is_active;

  return {
    courseData,
    mutate,
    userRegistered,
    copyCourseLink,
    favoriteLoading,
    setFavoriteAction,
    portalNode,
    playerRef,
    onSetSubmitRate,
    onUpdateSubmitRate,
    isExam,
    isMobile,
    isAdmin,
    hasLongHeader,
    step,
    theaterMode,
    loaded,
    nextLesson,
    goNext,
    timeUpdateHandler,
    setFavCourseStatus,
    watchedData,
    updateWatchTime,
    lessonChangeHandler,
    selectedLesson,
    onPrevLesson,
    onEnd,
    lesson,
    setIsResult,
    recentProduct,
    recommendedProduct,
    relatedProduct,
    instructorProduct,
    comments,
    setComments,
    videoTime,
    setVideoTime,
    stopAutoPlayLast,
    instructorPackage,
    relatedPackage,
    nextIsLast,
    goPrevLesson,
    isFirstLesson,
    isSidebarOpen,
    setIsSidebarOpen,
    isBottomExamVisible,
    setisBottomExamVisible,
    jumpVideoTime,
    setJumpVideoTime,
    clickNoteCourse,
    showNoteTab,
    setShowNoteTab,
    requestNext,
    setRequestNext,
    onPrevLessonHeadline,
    handleCertificatePreviewModal,
    shouldRenderSubscriptionTopBanner,
  };
};
